import React, { useState } from "react";
import { _apiPut } from "../../lib/apiClient";
import { CustomIcon } from "../shared/CustomIcon";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

// =====================================================================
// AvailabilityEditor — shown on MyProfilePage for users whose bookings
// power the public Pastor Directory. Lets the user define one or more
// time slots per weekday. Empty availability for a day = unavailable.
// Empty availability altogether = always available (back-compat default).
// =====================================================================
export default function AvailabilityEditor({ currentUser, setCurrentUser, users, setUsers }) {
  const me = users.find(u => u.id === currentUser.id) || currentUser;
  const initial = me.availability && typeof me.availability === "object" ? me.availability : {};
  const [hours, setHours] = useState(() => {
    const out = {};
    DAYS.forEach(d => { out[d.key] = Array.isArray(initial[d.key]) ? initial[d.key] : []; });
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const addSlot = (k) => setHours(h => ({ ...h, [k]: [...h[k], { start: "09:00", end: "17:00" }] }));
  const removeSlot = (k, i) => setHours(h => ({ ...h, [k]: h[k].filter((_, idx) => idx !== i) }));
  const updateSlot = (k, i, field, value) => setHours(h => ({
    ...h, [k]: h[k].map((s, idx) => idx === i ? { ...s, [field]: value } : s),
  }));

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      // Strip empty days so the server doesn't persist clutter.
      const payload = {};
      DAYS.forEach(d => { if (hours[d.key].length) payload[d.key] = hours[d.key]; });
      const res = await _apiPut("/me/availability", { availability: payload });
      // Reflect saved state locally so My Profile re-renders with new hours.
      const updated = users.map(u => u.id === me.id ? { ...u, availability: res.availability || {} } : u);
      setUsers(updated);
      setCurrentUser({ ...currentUser, availability: res.availability || {} });
      setMsg("saved: Saved — visitors will only be able to book within these hours.");
    } catch (e) {
      setMsg(`Could not save — ${e.message || "try again"}`);
    } finally { setSaving(false); }
  };

  return (
    <div className="card mb-20" data-testid="availability-editor">
      <div className="card-header">
        <div className="card-title">Booking Availability</div>
        <span style={{ fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
          Public visitors can only book inside these windows
        </span>
      </div>
      <div style={{ padding: "18px 24px" }}>
        {DAYS.map(d => (
          <div key={d.key} data-testid={`avail-day-${d.key}`} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <div style={{ width: 110, fontFamily: "'Montserrat',sans-serif", fontWeight: 700, color: "var(--kc-text)", fontSize: 13 }}>{d.label}</div>
            {(hours[d.key] || []).map((slot, i) => (
              <div key={`${d.key}-${i}`} data-testid={`avail-slot-${d.key}-${i}`} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 6 }}>
                <input
                  type="time"
                  value={slot.start}
                  data-testid={`avail-start-${d.key}-${i}`}
                  onChange={e => updateSlot(d.key, i, "start", e.target.value)}
                  style={{ width: 90, padding: "4px 8px", fontSize: 12, borderRadius: 4, border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", background: "transparent", color: "var(--kc-text)" }}
                />
                <span style={{ color: "color-mix(in srgb, var(--kc-accent) 70%, transparent)", fontSize: 12 }}>→</span>
                <input
                  type="time"
                  value={slot.end}
                  data-testid={`avail-end-${d.key}-${i}`}
                  onChange={e => updateSlot(d.key, i, "end", e.target.value)}
                  style={{ width: 90, padding: "4px 8px", fontSize: 12, borderRadius: 4, border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", background: "transparent", color: "var(--kc-text)" }}
                />
                <button
                  type="button"
                  data-testid={`avail-remove-${d.key}-${i}`}
                  onClick={() => removeSlot(d.key, i)}
                  style={{ background: "transparent", border: 0, color: "#FF6B6B", cursor: "pointer", fontSize: 14, fontWeight: 700 }}
                >×</button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline"
              data-testid={`avail-add-${d.key}`}
              onClick={() => addSlot(d.key)}
            >+ Slot</button>
            {(!hours[d.key] || hours[d.key].length === 0) && (
              <span style={{ fontSize: 11, color: "var(--kc-meta)", fontStyle: "italic" }}>Unavailable</span>
            )}
          </div>
        ))}
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button className="btn btn-gold" data-testid="avail-save" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Availability"}
          </button>
          {msg && (
            <span data-testid="avail-msg" style={{ fontSize: 12, color: msg.startsWith("saved:") ? "#A8D5BA" : "#FF6B6B", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {msg.startsWith("saved:") && <CustomIcon name="check" size={12} />}
              {msg.startsWith("saved:") ? msg.slice(6).trim() : msg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
